import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, ScrollRestoration } from 'react-router-dom';

import { Login } from '@/features/login';
import { Logout } from '@/features/logout';
import { ProtectedRoutes } from '@/features/protected-routes';
import { AppProvider } from '@/providers/app-provider';
import { AccessDenied } from '@flintmint/web-shared/components/access-denied';
import { Auth0InitGate } from '@flintmint/web-shared/components/auth0-init-gate';
import { EmailVerification } from '@flintmint/web-shared/components/email-verification';

import { MedicalModuleDashboard } from '@/features/medical-module-dashboard';
import { FlowDashboard } from '@/features/flow-dashboard';
import { UsersPage } from '@/features/users/users-page';
import { AccessListPage } from '@/features/access-list';
import { SettingsPage } from '@/features/settings';

import { appRoutes } from '@/constants/routes';
import { installStaleChunkReload, lazyPage } from '@/stale-chunk-reload';

const AiUsagePricingPage = lazyPage(() => import('@/features/ai-usage-pricing'), 'AiUsagePricingPage');
const UserDetailsPage = lazyPage(() => import('@/features/users/user-details-page'), 'UserDetailsPage');

import './index.scss';

const router = createBrowserRouter([
  {
    element: (
      <AppProvider>
        <ScrollRestoration />
        <Auth0InitGate accessDeniedPath={appRoutes.accessDenied} emailVerificationPath={appRoutes.emailVerification} />
      </AppProvider>
    ),
    children: [
      {
        path: appRoutes.login,
        element: <Login />,
      },
      {
        path: appRoutes.logout,
        element: <Logout />,
      },
      {
        path: appRoutes.accessDenied,
        element: <AccessDenied logoutPath={appRoutes.logout} />,
      },
      {
        path: appRoutes.emailVerification,
        element: <EmailVerification logoutPath={appRoutes.logout} />,
      },
      {
        path: '/',
        element: <ProtectedRoutes />,
        children: [
          {
            index: true,
            element: <Navigate to={appRoutes.flowModules} replace />,
          },
          {
            path: appRoutes.flowModules,
            element: <MedicalModuleDashboard />,
          },
          {
            path: appRoutes.flows,
            element: <FlowDashboard />,
          },
          {
            path: appRoutes.users,
            element: <UsersPage />,
          },
          {
            path: appRoutes.userDetails,
            element: <UserDetailsPage />,
          },
          {
            path: appRoutes.accessList,
            element: <AccessListPage />,
          },
          {
            path: appRoutes.aiUsagePricing,
            element: <AiUsagePricingPage />,
          },
          {
            path: appRoutes.settings,
            element: <SettingsPage />,
          },
          {
            path: appRoutes.legacyTokenUsage,
            element: <Navigate to={appRoutes.aiUsagePricing} replace />,
          },
          {
            path: '*',
            element: <Navigate to={appRoutes.flowModules} replace />,
          },
        ],
      },
    ],
  },
]);

installStaleChunkReload();

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
